import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Truck, Search, Check, ChevronDown, ChevronUp, Layers, Zap, 
  AlertCircle, CheckCircle2, ArrowRight, ArrowLeft,
  Info, Sparkles, Box, ExternalLink, Calculator
} from 'lucide-react';

// หมวดหมู่และข้อมูลจำเพาะเชิงปฏิบัติการของ Toyota Forklift
const CATEGORY_META = {
  'Reach truck': {
    nameTh: 'รถโฟล์คลิฟท์ยกสูงทางเดินแคบ (Reach Truck)',
    seriesTh: 'BT Reflex (R, E, N, O, B-series)',
    desc: 'ออกแบบสำหรับแร็คจัดเก็บทรงสูงในอาคาร ทางเดินแคบ 2.7 - 3.2 ม. ความสูงยกสูงสุดถึง 13.0 ม.',
    icon: '🏢',
    priority: 1,
    minRecHeight: 5500,
    maxRecHeight: 13000,
  },
  'Very Narrow Aisle': {
    nameTh: 'รถสำหรับช่องทางวิ่งแคบพิเศษ (VNA Truck)',
    seriesTh: 'BT Vector (A, C, V-series)',
    desc: 'สำหรับคลังสินค้าเพดานสูงพิเศษและความหนาแน่นสูงสุด ช่องวิ่งแคบเพียง 1.6 - 1.9 ม. ความสูงยกถึง 14.8 ม.',
    icon: '⚡',
    priority: 2,
    minRecHeight: 7000,
    maxRecHeight: 15000,
  },
  'Electric counterbalance': {
    nameTh: 'รถยกไฟฟ้า 3-4 ล้อ (Electric Counterbalance)',
    seriesTh: 'Toyota Traigo / 8FBE / 9FBE / 8FBN',
    desc: 'รถยกไฟฟ้าอเนกประสงค์ ไร้มลพิษ คล่องตัวสูง เหมาะสำหรับโหลดตู้คอนเทนเนอร์และแร็คระดับปานกลาง (3.0 - 6.5 ม.)',
    icon: '🔋',
    priority: 3,
    minRecHeight: 0,
    maxRecHeight: 6500,
  },
  'Pallet stacker': {
    nameTh: 'รถยกพาเลทไฟฟ้าซ้อนสูง (Pallet Stacker)',
    seriesTh: 'BT Staxio (SPE, SWE, S-series)',
    desc: 'รถยกขนาดกะทัดรัดแบบเดินตาม/ยืนขับ เหมาะกับแร็คเตี้ย-ปานกลาง (2.0 - 5.4 ม.) และช่องทางวิ่งแคบ',
    icon: '📦',
    priority: 4,
    minRecHeight: 0,
    maxRecHeight: 5400,
  },
  'Automated truck': {
    nameTh: 'รถยกอัตโนมัติไร้คนขับ (Automated AGV)',
    seriesTh: 'Toyota Autopilot (RAE, SAE, TAE)',
    desc: 'ระบบรถยกไร้คนขับอัตโนมัติ 24/7 เพื่อประสิทธิภาพสูงสุดและลดความผิดพลาดในการขนย้าย',
    icon: '🤖',
    priority: 5,
    minRecHeight: 0,
    maxRecHeight: 10000,
  },
  'Engine counterbalance': {
    nameTh: 'รถยกเครื่องยนต์ดีเซล/LPG (Engine Counterbalance)',
    seriesTh: 'Toyota Tonero / 8FD / 8FG',
    desc: 'สมรรถนะสูง ทนทาน เหมาะสำหรับการทำงานภายนอกอาคาร ลานสินค้า และงานหนักกลางแจ้ง',
    icon: '🚜',
    priority: 6,
    minRecHeight: 0,
    maxRecHeight: 6500,
  },
  'Order picker': {
    nameTh: 'รถหยิบสินค้าในแนวสูง (Order Picker)',
    seriesTh: 'BT Optio (OSE, OME, OAE)',
    desc: 'สำหรับงานหยิบสินค้าตามรายการ (Order Picking) รองรับการหยิบสินค้าระดับพื้นจนถึงระดับสูง 12 ม.',
    icon: '📋',
    priority: 7,
    minRecHeight: 0,
    maxRecHeight: 12000,
  },
  'Pallet truck': {
    nameTh: 'รถลากพาเลทไฟฟ้า (Powered Pallet Truck)',
    seriesTh: 'BT Levio (LPE, LWE)',
    desc: 'สำหรับเคลื่อนย้ายสินค้าระดับพื้นดินระยะกลาง-ไกล (ระดับพื้นดิน ไม่รองรับการยกขึ้นแร็ค)',
    icon: '🛒',
    priority: 8,
    isGroundOnly: true,
  },
  'Hand pallet truck': {
    nameTh: 'รถลากพาเลทแมนนวล (Hand Pallet Truck)',
    seriesTh: 'BT Lifter (LHM)',
    desc: 'รถลากพาเลทมาตรฐานระดับพื้นดิน ไม่ใช้ไฟฟ้า (ไม่รองรับการยกขึ้นแร็ค)',
    icon: '🔧',
    priority: 9,
    isGroundOnly: true,
  },
  'Towing tractor': {
    nameTh: 'รถลากจูงขบวนพาเลท (Towing Tractor)',
    seriesTh: 'TSE / 42-2TD',
    desc: 'สำหรับลากจูงขบวนรถเข็นและตู้สินค้าแนวนอนในโรงงาน (ไม่รองรับการยกขึ้นแร็ค)',
    icon: '🚚',
    priority: 10,
    isGroundOnly: true,
  },
};

// ฟังก์ชันตรวจจับข้อกำหนดหน้างานจาก mapData (อ่านค่าจาก Step 1 100%)
function detectWorkplaceRequirements(mapData) {
  let detectedHeight = 0;
  let rackSource = '';
  let detectedWeight = 0;
  let weightSource = '';
  let detectedAisle = 0;
  let hasRackData = false;

  // 1. ตรวจสอบข้อมูลพาเลท
  const primaryPallet = mapData?.palletTypes?.[0] || mapData?.palletSize || { width: 1000, length: 1200, maxWeight: 1000 };
  const palletWidth = Number(primaryPallet.width) || 1000;
  const palletLength = Number(primaryPallet.length) || 1200;

  if (primaryPallet && primaryPallet.maxWeight) {
    detectedWeight = Number(primaryPallet.maxWeight);
    weightSource = primaryPallet.name ? `${primaryPallet.name} (${detectedWeight.toLocaleString()} kg)` : `พาเลท ${detectedWeight.toLocaleString()} kg`;
  }

  // 2. ตรวจสอบพื้นที่แร็คที่วาดไว้ใน mapData.areas
  const rackAreas = (mapData?.areas || []).filter(a => 
    a.type?.startsWith('storage_') || a.type === 'storage'
  );

  if (rackAreas.length > 0) {
    let maxHeight = 0;
    let sourceAreaName = '';
    let minAisle = 9999;

    rackAreas.forEach((area, idx) => {
      const levels = area.rackConfig?.levels || 4;
      const beamThick = area.rackConfig?.beamThickness ?? 100;
      const airGap = area.rackConfig?.airGap ?? 300;
      const hasBottomBeam = area.rackConfig?.hasBottomBeam !== false;
      const palletH = primaryPallet?.height || primaryPallet?.totalHeight || 1400;
      
      const totalH = (hasBottomBeam ? beamThick : 0) + (levels * (palletH + airGap + beamThick));
      if (totalH > maxHeight) {
        maxHeight = totalH;
        sourceAreaName = area.label || `แร็คโซน #${idx + 1} (${levels} ชั้น)`;
      }
      if (area.rackConfig?.aisleWidth && area.rackConfig.aisleWidth < minAisle) {
        minAisle = area.rackConfig.aisleWidth;
      }
    });

    if (maxHeight > 0) {
      detectedHeight = maxHeight;
      rackSource = sourceAreaName;
      hasRackData = true;
    }
    if (minAisle < 9999) {
      detectedAisle = minAisle;
    }
  }

  return { 
    detectedHeight, 
    rackSource, 
    detectedWeight: detectedWeight || 1000, 
    weightSource: weightSource || 'พาเลทมาตรฐาน (1,000 kg)', 
    detectedAisle: detectedAisle || 3000, 
    hasRackData,
    pallet: {
      width: palletWidth,
      length: palletLength,
      name: primaryPallet.name || 'พาเลทมาตรฐาน'
    }
  };
}

// ฟังก์ชันคำนวณ Aisle Width (Ast) ตามสูตรวิศวกรรม Toyota Material Handling (VDI 2198)
// สูตรทางการ:
// R1 = SQRT((L - X)^2 + (W / 2)^2)
// Ast = Wa + R1 + 2S
// โดย:
// L = ความยาวพาเลทตามแนวทางเข้างา (Length of the load, mm)
// W = ความกว้างพาเลท (Width of the load, mm)
// Wa = รัศมีวงเลี้ยวของรถยก (Turning radius, mm)
// X = ระยะห่างจุดหมุนหรือเพลาขับถึงหน้างา (Load distance, mm)
// S = ระยะเผื่อความปลอดภัยด้านข้าง (Safety clearance, mm)
function calculateToyotaAst(forklift, pallet) {
  const L = Number(pallet?.length) || 1200;
  const W = Number(pallet?.width) || 1000;
  const cat = forklift.category || '';
  const Wa = Number(forklift.turning_radius_mm) || 1700;

  let X = 373;
  let S = 200;

  if (cat === 'Reach truck') {
    // Toyota Reach Truck (RRE ซีรีส์): X = 373mm, S = 200mm
    X = 373;
    S = 200;
  } else if (cat === 'Pallet stacker') {
    // Toyota Powered Stacker (SWE ซีรีส์): X = 660mm, S = 100mm
    X = 660;
    S = 100;
  } else if (cat === 'Electric counterbalance') {
    // Toyota Electric Counterbalance (8FB / 9FB ซีรีส์): X = -320mm, S = 50mm
    // หมายเหตุ: สำหรับรถถ่วงหลัง ระยะยื่นหน้างา (Overhang) จะบวกเข้ากับ L: (L - (-320)) = (L + 320)
    X = (forklift.load_capacity_kg && forklift.load_capacity_kg >= 2500) ? -370 : -320;
    S = 50;
  } else if (cat === 'Engine counterbalance') {
    // Toyota Engine Counterbalance (Tonero / 8FD ซีรีส์): X = -420mm, S = 50mm
    X = (forklift.load_capacity_kg && forklift.load_capacity_kg >= 2500) ? -480 : -420;
    S = 50;
  } else if (cat === 'Very Narrow Aisle') {
    // VNA (BT Vector): ใช้หัวหมุน Turret Head รถไม่ต้องเลี้ยวทั้งคันในช่องวิ่ง
    // Ast = ความกว้างตัวรถ + ระยะห่างรางนำทาง (Clearance)
    const vWidth = Number(forklift.overall_width_mm) || 1400;
    const astVna = Math.max(vWidth + 200, Math.max(L, W) + 200);
    return {
      ast: Math.round(astVna),
      R1: Math.round(W / 2),
      Wa,
      X: 0,
      S: 100,
      L,
      W,
      isVna: true
    };
  } else if (cat === 'Order picker') {
    X = 500;
    S = 100;
  } else {
    X = 660;
    S = 100;
  }

  // R1 = SQRT((L - X)^2 + (W / 2)^2)
  const R1 = Math.sqrt(Math.pow(L - X, 2) + Math.pow(W / 2, 2));
  // Ast = Wa + R1 + 2S
  const Ast = Wa + R1 + (2 * S);

  return {
    ast: Math.round(Ast),
    R1: Math.round(R1),
    Wa,
    X,
    S,
    L,
    W,
    isVna: false
  };
}

// คอมโพเนนต์รูปภาพรถยกขนาดกะทัดรัด พร้อม Fallback icon เมื่อรูปโหลดไม่ผ่าน
function CompactForkliftThumb({ src, alt }) {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <div className="w-8 h-8 bg-white/80 rounded border border-gray-200/80 flex items-center justify-center shrink-0 text-gray-400">
        <Truck size={15} />
      </div>
    );
  }

  return (
    <div className="w-8 h-8 bg-white/80 rounded border border-gray-200/80 flex items-center justify-center p-0.5 shrink-0">
      <img
        src={src}
        alt={alt}
        onError={() => setHasError(true)}
        className="max-h-full max-w-full object-contain mix-blend-multiply"
      />
    </div>
  );
}

export default function Configuration({ 
  config, 
  setConfig, 
  mapData, 
  selectedForklift, 
  setSelectedForklift,
  onNavigateTab 
}) {
  const [forklifts, setForklifts] = useState([]);
  const [loadingForklifts, setLoadingForklifts] = useState(false);

  // ดึงสเปกจากผังขั้นตอนที่ 1 โดยตรง (Read-Only)
  const req = useMemo(() => detectWorkplaceRequirements(mapData), [mapData]);
  const { hasRackData, detectedHeight, detectedWeight, detectedAisle, rackSource, weightSource, pallet } = req;

  // ตัวกรองและการค้นหา
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryTab, setSelectedCategoryTab] = useState('all');
  const [onlySuitable, setOnlySuitable] = useState(hasRackData);
  const [sortBy, setSortBy] = useState('recommended');
  const [collapsedCategories, setCollapsedCategories] = useState({});

  // ถ้าสถานะ hasRackData เปลี่ยน ให้อัปเดต onlySuitable ให้สอดคล้อง
  useEffect(() => {
    if (!hasRackData) {
      setOnlySuitable(false);
    } else {
      setOnlySuitable(true);
    }
  }, [hasRackData]);

  // โหลดรายการรถยก Toyota 190 รุ่นจากฐานข้อมูล
  useEffect(() => {
    const fetchForklifts = async () => {
      setLoadingForklifts(true);
      try {
        const res = await fetch('/api/forklifts');
        const json = await res.json();
        if (json.status === 'success' && json.data) {
          setForklifts(json.data);
        }
      } catch (err) {
        console.error('Error loading forklifts in config:', err);
      } finally {
        setLoadingForklifts(false);
      }
    };
    fetchForklifts();
  }, []);

  // ฟังก์ชันคำนวณความสามารถและความเหมาะสมของรถยกแต่ละรุ่น (ผสาน Ast คำนวณจริง)
  const evaluateForklift = useCallback((f) => {
    const isGroundOnly = ['Pallet truck', 'Hand pallet truck', 'Towing tractor'].includes(f.category);
    
    // หาความสูงยกสูงสุดจากทั้ง lift_height_mm และ mast_variants (เสากระโดงออปชัน)
    let maxMastMm = f.lift_height_mm || 0;
    if (Array.isArray(f.mast_variants) && f.mast_variants.length > 0) {
      const mastHeights = f.mast_variants.map(m => m.lift_height_mm || 0);
      maxMastMm = Math.max(maxMastMm, ...mastHeights);
    }
    if (maxMastMm === 0 && !isGroundOnly) maxMastMm = 3000;

    const loadCapKg = f.load_capacity_kg || 0;

    // คำนวณ Ast ตามขนาดตัวรถและขนาดของพาเลทจาก Step 1
    const astCalc = calculateToyotaAst(f, pallet);
    const calculatedAst = astCalc.ast;

    // ถ้ายังไม่มีข้อมูลแร็คจากหน้าแรก ไม่ทำการตัดสินว่าผ่าน/ไม่ผ่าน
    if (!hasRackData) {
      return {
        maxLiftMm: maxMastMm,
        loadCapacityKg: loadCapKg,
        calculatedAst,
        astCalc,
        passHeight: true,
        passWeight: true,
        passAisle: true,
        isSuitable: true,
        failReason: '',
        isGroundOnly
      };
    }

    // ตรวจสอบเงื่อนไขตามแร็คและพาเลทจาก Step 1
    // 1. ความสูงยก: รถต้องยกถึงความสูงแร็ค
    const passHeight = !isGroundOnly && maxMastMm >= detectedHeight;
    // 2. พิกัดน้ำหนัก: รถต้องยกน้ำหนักพาเลทรวมสินค้าได้
    const passWeight = loadCapKg >= detectedWeight;
    // 3. ช่องทางวิ่ง Ast: ความกว้างช่องทางวิ่งระหว่างแร็คต้องเพียงพอให้รถหมุนเลี้ยวได้
    const passAisle = !detectedAisle || calculatedAst <= detectedAisle;

    const isSuitable = passHeight && passWeight && passAisle && !isGroundOnly;

    // คำอธิบายเหตุผลที่ไม่ผ่าน
    let failReason = '';
    if (isGroundOnly) {
      failReason = 'ใช้งานระดับพื้น ไม่รองรับยกขึ้นแร็ค';
    } else {
      const reasons = [];
      if (!passHeight) {
        const diffM = ((detectedHeight - maxMastMm) / 1000).toFixed(1);
        reasons.push(`ยกไม่ถึง (ขาดอีก ${diffM}m)`);
      }
      if (!passWeight) {
        const diffKg = detectedWeight - loadCapKg;
        reasons.push(`รับน้ำหนักไม่พอ (ขาดอีก ${diffKg.toLocaleString()}kg)`);
      }
      if (!passAisle) {
        const diffMm = calculatedAst - detectedAisle;
        reasons.push(`ช่องวิ่งแคบไป (ต้องการ Ast ${calculatedAst.toLocaleString()}mm > ผังแร็ค ${detectedAisle.toLocaleString()}mm)`);
      }
      failReason = reasons.join(' • ');
    }

    return {
      maxLiftMm: maxMastMm,
      loadCapacityKg: loadCapKg,
      calculatedAst,
      astCalc,
      passHeight,
      passWeight,
      passAisle,
      isSuitable,
      failReason,
      isGroundOnly
    };
  }, [hasRackData, detectedHeight, detectedWeight, detectedAisle, pallet]);

  // ประเมินผลรถยกทั้งหมด
  const evaluatedForklifts = useMemo(() => {
    return forklifts.map(f => ({
      ...f,
      eval: evaluateForklift(f)
    }));
  }, [forklifts, evaluateForklift]);

  // สถิติแยกตามหมวดหมู่
  const categoryStats = useMemo(() => {
    const stats = {};
    Object.keys(CATEGORY_META).forEach(cat => {
      stats[cat] = { total: 0, suitable: 0 };
    });

    evaluatedForklifts.forEach(f => {
      const cat = f.category || 'Electric counterbalance';
      if (!stats[cat]) stats[cat] = { total: 0, suitable: 0 };
      stats[cat].total += 1;
      if (f.eval.isSuitable) stats[cat].suitable += 1;
    });

    return stats;
  }, [evaluatedForklifts]);

  const totalCount = evaluatedForklifts.length;
  const totalSuitableCount = useMemo(() => {
    return evaluatedForklifts.filter(f => f.eval.isSuitable).length;
  }, [evaluatedForklifts]);

  // กรองตามเงื่อนไขการค้นหาและฟิลเตอร์
  const filteredForklifts = useMemo(() => {
    let list = evaluatedForklifts;

    // กรองคำค้นหา
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(f => 
        (f.model || '').toLowerCase().includes(q) ||
        (f.series || '').toLowerCase().includes(q) ||
        (f.category || '').toLowerCase().includes(q)
      );
    }

    // กรองตามแท็บหมวดหมู่
    if (selectedCategoryTab !== 'all') {
      list = list.filter(f => f.category === selectedCategoryTab);
    }

    // กรองเฉพาะรุ่นที่ใช้งานได้ (ทำงานเฉพาะเมื่อมีข้อมูลแร็ค)
    if (hasRackData && onlySuitable) {
      list = list.filter(f => f.eval.isSuitable);
    }

    // จัดเรียงลำดับ
    list = [...list].sort((a, b) => {
      if (sortBy === 'recommended') {
        if (hasRackData && a.eval.isSuitable !== b.eval.isSuitable) {
          return a.eval.isSuitable ? -1 : 1;
        }
        const priorityA = CATEGORY_META[a.category]?.priority || 99;
        const priorityB = CATEGORY_META[b.category]?.priority || 99;
        if (priorityA !== priorityB) return priorityA - priorityB;
        return b.eval.maxLiftMm - a.eval.maxLiftMm;
      }
      if (sortBy === 'lift_desc') return b.eval.maxLiftMm - a.eval.maxLiftMm;
      if (sortBy === 'cap_desc') return b.eval.loadCapacityKg - a.eval.loadCapacityKg;
      if (sortBy === 'aisle_asc') {
        return a.eval.calculatedAst - b.eval.calculatedAst;
      }
      if (sortBy === 'model_asc') return (a.model || '').localeCompare(b.model || '');
      return 0;
    });

    return list;
  }, [evaluatedForklifts, searchQuery, selectedCategoryTab, hasRackData, onlySuitable, sortBy]);

  // จัดกลุ่มรถยกเป็นกล่องตามประเภท (Group by Category)
  const groupedForklifts = useMemo(() => {
    const groups = {};
    
    const sortedCategories = Object.keys(CATEGORY_META).sort((catA, catB) => {
      const metaA = CATEGORY_META[catA];
      const metaB = CATEGORY_META[catB];
      
      if (hasRackData && detectedHeight >= 6000) {
        if (catA === 'Reach truck') return -1;
        if (catB === 'Reach truck') return 1;
        if (catA === 'Very Narrow Aisle') return -1;
        if (catB === 'Very Narrow Aisle') return 1;
      }
      return (metaA.priority || 99) - (metaB.priority || 99);
    });

    sortedCategories.forEach(cat => {
      const items = filteredForklifts.filter(f => f.category === cat);
      if (items.length > 0 || !onlySuitable) {
        groups[cat] = items;
      }
    });

    filteredForklifts.forEach(f => {
      if (!CATEGORY_META[f.category]) {
        if (!groups[f.category || 'Other']) groups[f.category || 'Other'] = [];
        if (!groups[f.category || 'Other'].includes(f)) {
          groups[f.category || 'Other'].push(f);
        }
      }
    });

    return groups;
  }, [filteredForklifts, hasRackData, detectedHeight, onlySuitable]);

  // การเลือกรุ่นรถยก
  const handleSelectForklift = (forklift) => {
    if (setSelectedForklift) {
      setSelectedForklift(forklift);
    }
    setConfig(prev => ({
      ...prev,
      selectedForkliftId: forklift.id,
      agvWidth: forklift.overall_width_mm || 1000,
      agvLength: forklift.overall_length_mm || 1600,
      agvSpeed: forklift.travel_speed_loaded_kmh 
        ? Math.round((forklift.travel_speed_loaded_kmh / 3.6) * 10) / 10 
        : 1.5,
    }));
  };

  const toggleCategoryCollapse = (cat) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [cat]: !prev[cat]
    }));
  };

  const handleFlowChange = (flowId, key, value) => {
    setConfig(prev => ({
      ...prev,
      flowSettings: {
        ...(prev.flowSettings || {}),
        [flowId]: {
          ...((prev.flowSettings || {})[flowId] || {}),
          [key]: value
        }
      }
    }));
  };

  const handleChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 bg-slate-50/50 min-h-screen pb-20">
      
      {/* ─── Header & Breadcrumb ────────────────────────────────────── */}
      <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs mb-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-50 text-red-700 text-xs font-bold border border-red-200/60 mb-2">
              <Truck size={13} className="text-red-600" />
              <span>ขั้นตอนที่ 2: เลือกรุ่นรถยก Toyota (Equipment Matching)</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">
              โมเดลรถยก Toyota ที่เหมาะสมกับแร็คและพาเลท
            </h1>
            <p className="text-xs text-gray-500 mt-1">
              คำนวณขนาดช่องวิ่ง Ast อัตโนมัติจากขนาดรถและขนาดพาเลทตามสูตร Toyota พร้อมประเมินความสูงยกและพิกัดน้ำหนัก
            </p>
          </div>

          {/* Quick Step Switcher */}
          <div className="flex items-center gap-2 shrink-0">
            {onNavigateTab && (
              <button
                type="button"
                onClick={() => onNavigateTab('setup')}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 rounded-xl text-xs font-semibold shadow-xs transition-colors cursor-pointer"
              >
                <ArrowLeft size={14} />
                <span>1. ผัง & แร็ค</span>
              </button>
            )}
            {onNavigateTab && (
              <button
                type="button"
                onClick={() => onNavigateTab('simulation')}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-xs transition-colors cursor-pointer"
              >
                <span>3. จำลองทราฟฟิก</span>
                <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ─── 1. Workplace Requirements Header (Strictly Read-Only from Step 1) ─── */}
      {!hasRackData ? (
        /* กรณีที่ 1: ยังไม่ได้วาดแร็คใน Step 1 */
        <div className="bg-amber-50/90 border border-amber-200/80 rounded-2xl p-5 mb-6 text-center space-y-3 shadow-xs">
          <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto">
            <Layers size={22} />
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-black text-gray-900">
              ยังไม่ได้ระบุพื้นที่แร็คจัดเก็บในขั้นตอนที่ 1
            </h3>
            <p className="text-xs text-gray-600 max-w-lg mx-auto leading-relaxed">
              ในตอนเริ่มแรกถ้ายังไม่ได้วาดพื้นที่แร็คหรือระบุความสูงที่ต้องการยกในขั้นตอนที่ 1 
              ระบบจะยังไม่คัดกรองโมเดลรถยกที่ใช้งานได้ ท่านสามารถกลับไปวาดพื้นที่แร็ค (Selective Rack / Shuttle Rack) 
              หรือเลือกดูรายการรถยกโตโยต้าทั้งหมดด้านล่างนี้ได้
            </p>
          </div>
          {onNavigateTab && (
            <div className="pt-2">
              <button
                type="button"
                onClick={() => onNavigateTab('setup')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-xs transition-colors cursor-pointer"
              >
                <ArrowLeft size={14} />
                <span>ไปที่ขั้นตอนที่ 1: วาดพื้นที่แร็ค & กำหนดพาเลท</span>
              </button>
            </div>
          )}
        </div>
      ) : (
        /* กรณีที่ 2: มีข้อมูลแร็คจาก Step 1 แล้ว -> แสดงข้อมูล Read-Only สวยงาม ไม่ต้องกรอกซ้ำ */
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-5 rounded-2xl shadow-md border border-slate-700 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3.5 border-b border-slate-700/80">
            <div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                <h2 className="text-xs sm:text-sm font-extrabold text-white tracking-wide uppercase">
                  ข้อกำหนดหน้างานจากผังขั้นตอนที่ 1 (Workplace Criteria from Layout)
                </h2>
              </div>
              <p className="text-[11px] text-slate-300 mt-0.5">
                ดึงข้อมูลความสูงแร็ค, น้ำหนัก และขนาดพาเลท {pallet.width}×{pallet.length} mm เพื่อคำนวณ Ast รถยกตามสูตร Toyota
              </p>
            </div>

            {onNavigateTab && (
              <button
                type="button"
                onClick={() => onNavigateTab('setup')}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-200 rounded-lg text-xs font-semibold transition-colors cursor-pointer self-start sm:self-auto shrink-0"
              >
                <span>แก้ไขในขั้นตอนที่ 1</span>
                <ExternalLink size={12} />
              </button>
            )}
          </div>

          {/* 3 Read-Only Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3.5">
            {/* Card 1: Rack Height */}
            <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-300 flex items-center gap-1.5">
                  <Layers size={13} className="text-red-400" />
                  ความสูงแร็คจัดเก็บ
                </span>
                <span className="text-xs font-black text-amber-300 bg-amber-400/10 border border-amber-400/30 px-1.5 py-0.5 rounded">
                  {(detectedHeight / 1000).toFixed(1)} ม.
                </span>
              </div>
              <div className="text-lg font-black text-white mt-1">
                {detectedHeight.toLocaleString()} <span className="text-xs font-normal text-slate-400">mm</span>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5 truncate" title={rackSource}>
                {rackSource}
              </div>
            </div>

            {/* Card 2: Pallet Weight & Size */}
            <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-300 flex items-center gap-1.5">
                  <Box size={13} className="text-blue-400" />
                  น้ำหนัก & ขนาดพาเลท
                </span>
                <span className="text-xs font-black text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 px-1.5 py-0.5 rounded">
                  {(detectedWeight / 1000).toFixed(1)} ตัน
                </span>
              </div>
              <div className="text-lg font-black text-white mt-1">
                {detectedWeight.toLocaleString()} <span className="text-xs font-normal text-slate-400">kg</span>
                <span className="text-xs font-semibold text-blue-300 ml-2">
                  ({pallet.width}×{pallet.length} mm)
                </span>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5 truncate" title={weightSource}>
                {weightSource} • ใช้คำนวณ Ast ร่วมกับตัวรถ
              </div>
            </div>

            {/* Card 3: Min Aisle Width */}
            <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-300 flex items-center gap-1.5">
                  <Truck size={13} className="text-emerald-400" />
                  ช่องวิ่งแร็คผังงาน
                </span>
                <span className="text-xs font-black text-slate-200 bg-slate-700 px-1.5 py-0.5 rounded">
                  {(detectedAisle / 1000).toFixed(1)} ม.
                </span>
              </div>
              <div className="text-lg font-black text-white mt-1">
                {detectedAisle.toLocaleString()} <span className="text-xs font-normal text-slate-400">mm</span>
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">
                ช่องทางวิ่งระหว่างแถวแร็คที่วาดใน Step 1
              </div>
            </div>
          </div>

          <div className="mt-3 pt-2.5 border-t border-slate-700/60 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-400">
            <span className="flex items-center gap-1.5">
              <Info size={12} className="text-slate-400 shrink-0" />
              <span>
                สูตรคำนวณ Ast มาตรฐาน Toyota: <strong>Ast = Wa + &radic;((L - X)&sup2; + (W/2)&sup2;) + 2S</strong> (คำนวณตามขนาดรถและพาเลท {pallet.width}&times;{pallet.length} mm)
              </span>
            </span>
            <span className="text-emerald-400 font-bold">
              ผ่านเกณฑ์ {totalSuitableCount} จาก {totalCount} รุ่น
            </span>
          </div>
        </div>
      )}

      {/* ─── Currently Selected Toyota Forklift Spotlight ────────────── */}
      {selectedForklift && (
        <div className="bg-white p-4 rounded-xl border-2 border-emerald-500/40 shadow-xs mb-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 bg-emerald-600 text-white text-[9px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-bl-lg flex items-center gap-1">
            <Check size={11} /> รุ่นที่เลือกใช้งาน (Selected Model)
          </div>

          {(() => {
            const ev = evaluateForklift(selectedForklift);
            const astInfo = ev.astCalc;

            return (
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <CompactForkliftThumb src={selectedForklift.image_url} alt={selectedForklift.model} />

                <div className="flex-1 text-center sm:text-left">
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-1.5">
                    <span className="text-[10px] font-black text-red-600 bg-red-50 border border-red-200 px-2 py-0.2 rounded">
                      {selectedForklift.series || 'Series'}
                    </span>
                    <span className="text-[11px] text-gray-600 font-semibold">
                      {selectedForklift.category || 'Forklift'}
                    </span>
                    {hasRackData && (
                      ev.isSuitable ? (
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.2 rounded-full flex items-center gap-1">
                          <CheckCircle2 size={11} /> ตรงตามสเปกแร็คและช่องวิ่ง
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.2 rounded-full flex items-center gap-1">
                          <AlertCircle size={11} /> {ev.failReason}
                        </span>
                      )
                    )}
                  </div>

                  <h3 className="text-base font-black text-gray-900 mt-0.5">{selectedForklift.model}</h3>

                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1 mt-1 text-xs text-gray-600">
                    <span>ยกสูง: <strong className="text-gray-900">{((selectedForklift.lift_height_mm || 3000)/1000).toFixed(1)}m</strong></span>
                    <span>•</span>
                    <span>พิกัด: <strong className="text-gray-900">{selectedForklift.load_capacity_kg}kg</strong></span>
                    <span>•</span>
                    <span title={`สูตร: Wa(${astInfo.Wa}) + R1(${astInfo.R1}) + 2S(${2*astInfo.S}) สำหรับพาเลท ${astInfo.W}×${astInfo.L}mm`}>
                      Ast คำนวณจริง: <strong className="text-emerald-700 font-black">{ev.calculatedAst.toLocaleString()} mm</strong>
                      {detectedAisle > 0 && (
                        <span className="text-[10px] text-gray-400 ml-1">(ผังแร็ค {detectedAisle.toLocaleString()} mm)</span>
                      )}
                    </span>
                    <span>•</span>
                    <span>ความเร็ว: <strong className="text-gray-900">{selectedForklift.travel_speed_loaded_kmh || '-'}km/h</strong></span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ─── 2. Filter & Controls Bar ────────────────────────────────── */}
      <div className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-xs mb-5 space-y-2.5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2.5">
          
          {/* Search Box */}
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาชื่อรุ่น หรือ ซีรีส์ เช่น RRE160, 9FBE20, BT Reflex..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-50 hover:bg-gray-100/80 focus:bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Suitable Only Toggle (Active only when hasRackData is true) */}
            {hasRackData ? (
              <label className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-bold cursor-pointer transition-colors ${
                onlySuitable 
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-800' 
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
              }`}>
                <input
                  type="checkbox"
                  checked={onlySuitable}
                  onChange={e => setOnlySuitable(e.target.checked)}
                  className="w-3.5 h-3.5 text-emerald-600 rounded focus:ring-emerald-500 cursor-pointer"
                />
                <span>แสดงเฉพาะรุ่นที่ใช้งานได้ ({totalSuitableCount} รุ่น)</span>
              </label>
            ) : (
              <span className="text-[11px] text-gray-400 bg-gray-50 border px-2.5 py-1.5 rounded-lg">
                แสดงรถทั้งหมด 190 รุ่น (ยังไม่มีสเปกแร็ค)
              </span>
            )}

            {/* Sort Dropdown */}
            <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-600">
              <span className="text-[10px] text-gray-400">เรียง:</span>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="bg-transparent font-semibold text-gray-800 text-xs focus:outline-none cursor-pointer"
              >
                <option value="recommended">ความเหมาะสมตามงาน (แนะนำ)</option>
                <option value="aisle_asc">ช่องวิ่ง Ast คำนวณจริง (แคบ → กว้าง)</option>
                <option value="lift_desc">ความสูงยก (มาก → น้อย)</option>
                <option value="cap_desc">พิกัดน้ำหนักยก (มาก → น้อย)</option>
                <option value="model_asc">ชื่อรุ่น (A → Z)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto pt-1.5 border-t border-gray-100 scrollbar-none text-xs">
          <button
            type="button"
            onClick={() => setSelectedCategoryTab('all')}
            className={`px-2.5 py-1 rounded-lg font-bold whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1.5 ${
              selectedCategoryTab === 'all'
                ? 'bg-gray-900 text-white shadow-xs'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <span>ทุกประเภท</span>
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
              selectedCategoryTab === 'all' ? 'bg-gray-800 text-gray-200' : 'bg-gray-200 text-gray-600'
            }`}>
              {hasRackData && onlySuitable ? totalSuitableCount : totalCount}
            </span>
          </button>

          {Object.keys(CATEGORY_META).map(cat => {
            const meta = CATEGORY_META[cat];
            const stats = categoryStats[cat] || { total: 0, suitable: 0 };
            const isActive = selectedCategoryTab === cat;
            const count = (hasRackData && onlySuitable) ? stats.suitable : stats.total;

            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategoryTab(cat)}
                className={`px-2.5 py-1 rounded-lg font-bold whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1.5 ${
                  isActive
                    ? 'bg-red-600 text-white shadow-xs'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                <span>{meta.icon}</span>
                <span>{cat}</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                  isActive ? 'bg-red-700 text-red-100' : 'bg-gray-200 text-gray-600'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── 3. Categorized Forklift Boxes & ULTRA-COMPACT Cards ─────── */}
      {loadingForklifts ? (
        <div className="bg-white p-10 rounded-2xl border border-gray-200 text-center space-y-2">
          <Truck size={30} className="text-red-500 animate-bounce mx-auto" />
          <div className="text-xs font-bold text-gray-700">กำลังโหลดรายการรถยก Toyota 190 โมเดล...</div>
        </div>
      ) : filteredForklifts.length === 0 ? (
        <div className="bg-white p-10 rounded-2xl border border-gray-200 text-center space-y-3">
          <AlertCircle size={36} className="text-amber-500 mx-auto" />
          <h4 className="text-sm font-bold text-gray-900">ไม่พบรุ่นรถยกที่ตรงกับเงื่อนไข</h4>
          <p className="text-xs text-gray-500 max-w-sm mx-auto">
            {hasRackData 
              ? `ความสูงแร็ค ${detectedHeight} mm, น้ำหนัก ${detectedWeight} kg หรือช่องวิ่ง ${detectedAisle} mm อาจเกินพิกัดของกลุ่มรถนี้` 
              : 'ลองค้นหาด้วยคำอื่น หรือเลือกหมวดหมู่อื่น'}
          </p>
          {hasRackData && onlySuitable && (
            <button
              type="button"
              onClick={() => setOnlySuitable(false)}
              className="px-3.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-xs font-bold transition-colors cursor-pointer"
            >
              แสดงรถทุกรุ่นในหมวดนี้
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedForklifts).map(([category, items]) => {
            if (items.length === 0) return null;
            const meta = CATEGORY_META[category] || {
              nameTh: category,
              seriesTh: '',
              desc: 'รถยกโตโยต้า',
              icon: '🚛'
            };
            const isCollapsed = collapsedCategories[category];
            const suitableInThisCat = items.filter(f => f.eval.isSuitable).length;
            const isHighlightedCategory = hasRackData && detectedHeight >= 6000 && (category === 'Reach truck' || category === 'Very Narrow Aisle');

            return (
              <section 
                key={category} 
                className={`bg-white rounded-xl border transition-shadow overflow-hidden ${
                  isHighlightedCategory 
                    ? 'border-red-200 shadow-xs ring-1 ring-red-100' 
                    : 'border-gray-200 shadow-xs'
                }`}
              >
                {/* Compact Category Box Header */}
                <div 
                  onClick={() => toggleCategoryCollapse(category)}
                  className={`px-3.5 py-2.5 flex items-center justify-between gap-3 cursor-pointer transition-colors ${
                    isHighlightedCategory 
                      ? 'bg-gradient-to-r from-red-50/60 to-white hover:bg-red-50/80' 
                      : 'bg-gray-50/70 hover:bg-gray-100/60'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="text-lg shrink-0">{meta.icon}</span>
                    <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                      <h3 className="text-xs sm:text-sm font-black text-gray-900 truncate">{meta.nameTh}</h3>
                      {meta.seriesTh && (
                        <span className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.2 rounded shrink-0">
                          {meta.seriesTh}
                        </span>
                      )}
                      {isHighlightedCategory && (
                        <span className="text-[9px] font-extrabold text-amber-800 bg-amber-100/80 border border-amber-300 px-1.5 py-0.2 rounded-full flex items-center gap-0.5 shrink-0">
                          <Sparkles size={10} className="text-amber-600" /> แนะนำสำหรับแร็ค {(detectedHeight/1000).toFixed(1)}m
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {hasRackData ? (
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md border ${
                        suitableInThisCat > 0 
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-200' 
                          : 'bg-gray-100 text-gray-600 border-gray-200'
                      }`}>
                        ใช้งานได้ {suitableInThisCat}/{items.length}
                      </span>
                    ) : (
                      <span className="text-[11px] font-medium text-gray-500 bg-white border px-2 py-0.5 rounded-md">
                        {items.length} รุ่น
                      </span>
                    )}
                    <button 
                      type="button" 
                      className="text-gray-400 hover:text-gray-600 p-0.5"
                      aria-label="Toggle category"
                    >
                      {isCollapsed ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
                    </button>
                  </div>
                </div>

                {/* Ultra-Compact Items Grid (up to 6 columns on desktop, narrower cards) */}
                {!isCollapsed && (
                  <div className="p-2.5 border-t border-gray-100 bg-slate-50/20">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                      {items.map(f => {
                        const isSelected = selectedForklift?.id === f.id;
                        const ev = f.eval;
                        const astDetails = ev.astCalc;

                        // สีพื้นหลังและขอบ: เขียวอ่อนเมื่อผ่านเกณฑ์, แดงอ่อนเมื่อไม่ผ่านเกณฑ์
                        let cardBgBorder = 'bg-white border-gray-200 hover:border-gray-300';
                        if (hasRackData) {
                          if (ev.isSuitable) {
                            cardBgBorder = 'bg-emerald-50/80 border-emerald-300/90 hover:border-emerald-400 hover:bg-emerald-50';
                          } else {
                            cardBgBorder = 'bg-rose-50/75 border-rose-200 hover:border-rose-300 hover:bg-rose-50';
                          }
                        }
                        if (isSelected) {
                          cardBgBorder += ' ring-2 ring-emerald-500 border-emerald-500 shadow-xs';
                        }

                        return (
                          <div
                            key={f.id}
                            className={`rounded-lg border p-2 flex flex-col justify-between transition-all relative ${cardBgBorder}`}
                          >
                            {/* Selected Active Tag */}
                            {isSelected && (
                              <div className="absolute -top-2 right-1.5 bg-emerald-600 text-white text-[8px] font-bold px-1.5 py-0.2 rounded-full shadow-xs flex items-center gap-0.5">
                                <Check size={9} /> ใช้งานอยู่
                              </div>
                            )}

                            <div>
                              {/* Top Row: Series & Model + Thumb */}
                              <div className="flex items-start justify-between gap-1 mb-1">
                                <div className="min-w-0 flex-1">
                                  <span className="text-[8px] font-bold text-gray-400 uppercase tracking-wider block truncate">
                                    {f.series || f.category}
                                  </span>
                                  <h4 className="text-xs font-black text-gray-900 leading-tight truncate" title={f.model}>
                                    {f.model}
                                  </h4>
                                  {hasRackData && !ev.isSuitable && (
                                    <div className="text-[9px] font-semibold text-rose-600 truncate mt-0.5" title={ev.failReason}>
                                      {ev.failReason}
                                    </div>
                                  )}
                                </div>

                                <CompactForkliftThumb src={f.image_url} alt={f.model} />
                              </div>

                              {/* Compact Specs Table (Key Metrics) */}
                              <div className="grid grid-cols-2 gap-x-1.5 gap-y-0.5 text-[10px] py-1 my-1 border-y border-black/5">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-400 text-[9px]">ยกสูง:</span>
                                  <span className={`font-bold ${hasRackData && !ev.passHeight ? 'text-rose-600' : 'text-gray-900'}`}>
                                    {(ev.maxLiftMm / 1000).toFixed(1)}m
                                  </span>
                                </div>

                                <div className="flex items-center justify-between">
                                  <span className="text-gray-400 text-[9px]">พิกัด:</span>
                                  <span className={`font-bold ${hasRackData && !ev.passWeight ? 'text-rose-600' : 'text-gray-900'}`}>
                                    {ev.loadCapacityKg.toLocaleString()}kg
                                  </span>
                                </div>

                                <div 
                                  className="flex items-center justify-between cursor-help" 
                                  title={`Ast คำนวณจริงตามสูตร Toyota:\nWa: ${astDetails.Wa} mm\n+ R1: ${astDetails.R1} mm (คำนวณจากพาเลท ${astDetails.W}×${astDetails.L} mm, X: ${astDetails.X} mm)\n+ 2S: ${astDetails.S * 2} mm\n= ${ev.calculatedAst.toLocaleString()} mm`}
                                >
                                  <span className="text-gray-400 text-[9px]">Ast:</span>
                                  <span className={`font-bold ${hasRackData && !ev.passAisle ? 'text-rose-600 font-extrabold' : 'text-gray-900'}`}>
                                    {ev.calculatedAst.toLocaleString()}
                                  </span>
                                </div>

                                <div className="flex items-center justify-between">
                                  <span className="text-gray-400 text-[9px]">สปีด:</span>
                                  <span className="font-medium text-gray-700">
                                    {f.travel_speed_loaded_kmh ? `${f.travel_speed_loaded_kmh}k` : '-'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Ultra-Compact Select Button */}
                            <button
                              type="button"
                              onClick={() => handleSelectForklift(f)}
                              className={`w-full py-1 px-1 rounded text-[10px] font-bold transition-all cursor-pointer flex items-center justify-center gap-1 mt-0.5 ${
                                isSelected
                                  ? 'bg-emerald-600 text-white shadow-xs'
                                  : hasRackData && ev.isSuitable
                                    ? 'bg-red-600 hover:bg-red-700 text-white shadow-xs'
                                    : hasRackData && !ev.isSuitable
                                      ? 'bg-white hover:bg-rose-100 text-rose-700 border border-rose-200'
                                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200'
                              }`}
                            >
                              {isSelected ? (
                                <>
                                  <Check size={11} />
                                  <span>ใช้งานอยู่</span>
                                </>
                              ) : hasRackData && ev.isSuitable ? (
                                <span>เลือกรุ่นนี้</span>
                              ) : (
                                <span>{hasRackData ? 'เลือก (ไม่ผ่าน)' : 'เลือกรุ่นนี้'}</span>
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* ─── 4. Fleet Setup & Simulation Parameters ──────────────────── */}
      <div className="mt-8 bg-white p-5 rounded-2xl border border-gray-200 shadow-xs space-y-6">
        <div className="border-b pb-3">
          <h2 className="text-base font-black text-gray-900">
            การตั้งค่าฝูงบินและการจำลองทราฟฟิก (Fleet & Simulation Engine)
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            ปรับแต่งจำนวนรถในระบบ, ความเร็วในการจำลอง, ขนาดตัวรถ 3D, และเส้นทางการไหลของสินค้า
          </p>
        </div>

        {/* Section: Fleet Size */}
        <section>
          <h3 className="text-xs font-bold text-gray-800 mb-2">จำนวนรถในฝูงบิน (Fleet Setup)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">
                จำนวนรถยกที่ปฏิบัติการพร้อมกัน (Active Vehicles)
              </label>
              <div className="flex items-center gap-2">
                <input 
                  type="number" 
                  min="1" 
                  max="50" 
                  className="w-24 border border-gray-300 rounded-lg p-1.5 text-xs font-bold text-gray-900 focus:ring-2 focus:ring-red-500"
                  value={config.fleetSize || 3}
                  onChange={(e) => handleChange('fleetSize', parseInt(e.target.value) || 1)}
                />
                <span className="text-xs text-gray-500">คัน (ในผังคลังสินค้า)</span>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Simulation Speed */}
        <section>
          <h3 className="text-xs font-bold text-gray-800 mb-1.5">ความเร็วในการจำลอง (Simulation Engine Speed)</h3>
          <div className="space-y-1 max-w-xs">
            <div className="flex items-center space-x-2">
              <input 
                type="range" 
                min="1" 
                max="10" 
                step="1" 
                value={config.simSpeed || 1} 
                onChange={(e) => handleChange('simSpeed', parseInt(e.target.value))} 
                className="w-full cursor-pointer accent-red-600" 
              />
              <span className="text-xs font-mono font-bold bg-gray-100 border px-2 py-0.5 rounded w-12 text-center">
                {config.simSpeed || 1}x
              </span>
            </div>
          </div>
        </section>

        {/* Section: Physical Dimensions */}
        <section>
          <h3 className="text-xs font-bold text-gray-800 mb-1">ขนาดกายภาพและความเร็ว (Physical Dimensions & Speed)</h3>
          <p className="text-[11px] text-gray-400 mb-2">
            ค่าเหล่านี้จะอัปเดตอัตโนมัติตามรุ่นรถยก Toyota ที่เลือก
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div>
              <label className="block text-gray-600 mb-1">ความกว้างตัวรถ (Width mm)</label>
              <input 
                type="number" 
                min="100" 
                className="w-full border border-gray-300 rounded-lg p-1.5 text-xs font-semibold focus:ring-2 focus:ring-red-500"
                value={config.agvWidth || 800}
                onChange={(e) => handleChange('agvWidth', parseInt(e.target.value) || 800)}
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">ความยาวตัวรถ (Length mm)</label>
              <input 
                type="number" 
                min="100" 
                className="w-full border border-gray-300 rounded-lg p-1.5 text-xs font-semibold focus:ring-2 focus:ring-red-500"
                value={config.agvLength || 1200}
                onChange={(e) => handleChange('agvLength', parseInt(e.target.value) || 1200)}
              />
            </div>
            <div>
              <label className="block text-gray-600 mb-1">ความเร็วสูงสุด (Speed m/s)</label>
              <input 
                type="number" 
                min="0.1" 
                step="0.1" 
                className="w-full border border-gray-300 rounded-lg p-1.5 text-xs font-semibold focus:ring-2 focus:ring-red-500"
                value={config.agvSpeed || 1.5}
                onChange={(e) => handleChange('agvSpeed', parseFloat(e.target.value) || 1.5)}
              />
            </div>
          </div>
        </section>

        {/* Section: 3D Color */}
        <section>
          <h3 className="text-xs font-bold text-gray-800 mb-2">สีตัวรถในระบบจำลอง 3D</h3>
          <div className="flex items-center gap-2.5">
            {[
              { name: 'Toyota Red', hex: '#c8102e' },
              { name: 'Safety Orange', hex: '#f97316' },
              { name: 'BT Reflex Yellow', hex: '#eab308' },
              { name: 'Corporate Blue', hex: '#0284c7' },
              { name: 'Eco Green', hex: '#10b981' },
              { name: 'Industrial Dark', hex: '#334155' }
            ].map(color => (
              <button
                key={color.hex}
                onClick={() => handleChange('agvColor', color.hex)}
                className={`w-7 h-7 rounded-full border-2 focus:outline-none transition-transform cursor-pointer ${
                  config.agvColor === color.hex ? 'border-gray-900 scale-110 shadow-md ring-2 ring-red-400' : 'border-transparent hover:scale-105'
                }`}
                style={{ backgroundColor: color.hex }}
                title={color.name}
              />
            ))}
          </div>
        </section>

        {/* Section: Material Flows */}
        {mapData?.flows && mapData.flows.length > 0 && (
          <section>
            <h3 className="text-xs font-bold text-gray-800 mb-2">เส้นทางการไหลของสินค้า (Material Flows)</h3>
            <div className="space-y-2">
              {mapData.flows.map((flow, index) => {
                const flowConfig = (config.flowSettings || {})[flow.id] || { 
                  orderRate: config.orderRate || 10, 
                  storageLogic: config.storageLogic || 'closest',
                  retrievalLogic: config.retrievalLogic || 'fifo',
                  orderGenerationType: 'random'
                };

                return (
                  <div key={flow.id} className="bg-gray-50 p-3 rounded-xl border border-gray-200 text-xs">
                    <h4 className="font-bold text-gray-900 mb-1.5 pb-1 border-b">
                      Flow #{index + 1}: อัตราการรับ-ส่งสินค้า
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <label className="block text-gray-600 mb-1">Incoming Order Rate (Pallets/Hour)</label>
                        <input 
                          type="number" 
                          min="1" 
                          className="w-full border rounded-lg p-1.5 text-xs bg-white font-semibold"
                          value={flowConfig.orderRate}
                          onChange={(e) => handleFlowChange(flow.id, 'orderRate', parseInt(e.target.value) || 1)}
                        />
                      </div>
                      <div>
                        <label className="block text-gray-600 mb-1">Order Generation Type</label>
                        <select 
                          className="w-full border rounded-lg p-1.5 text-xs bg-white font-semibold"
                          value={flowConfig.orderGenerationType || 'random'}
                          onChange={(e) => handleFlowChange(flow.id, 'orderGenerationType', e.target.value)}
                        >
                          <option value="random">Random (Realistic)</option>
                          <option value="constant">Constant</option>
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {/* ─── Bottom Navigation Bar ───────────────────────────────────── */}
      {onNavigateTab && (
        <div className="mt-6 flex items-center justify-between bg-white p-4 rounded-2xl border border-gray-200 shadow-xs">
          <button
            type="button"
            onClick={() => onNavigateTab('setup')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-colors cursor-pointer"
          >
            <ArrowLeft size={14} />
            <span>ย้อนกลับ: 1. ผัง, พาเลท & แร็ค</span>
          </button>

          <button
            type="button"
            onClick={() => onNavigateTab('simulation')}
            className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-extrabold shadow-sm transition-colors cursor-pointer"
          >
            <span>ต่อไป: 3. จำลองทราฟฟิก (Simulation)</span>
            <ArrowRight size={14} />
          </button>
        </div>
      )}

    </div>
  );
}
